import React, { useState } from 'react';

import './DataTable.scss';
import Button from '../button';
import {
  sanitizeChildren,
  findNestedObjectValue,
} from '../../containers/helpers';
import { TextView } from '../view-fields';

export interface FieldProps<T> {
  source: string;
  format?: string;
  label: string;
  component: T;
  className?: string;
  noLink?: boolean;
  route?: string;
}
export const Field: React.SFC<FieldProps<any>> = (props) => {
  return <>{props.children}</>;
};

export const Table: React.SFC = (props) => {
  return <div className="table table--responsive">{props.children}</div>;
};

export const TableHead: React.SFC = (props) => {
  return <div className="table-thead">{props.children}</div>;
};

type SortOrder = 'ASC' | 'DESC';

interface TableHeadItemProps {
  onClick?: (order: SortOrder) => void;
  children?: any;
  hideAction?: boolean;
  hideSort?: boolean;
  dataHead?: string;
}

export const TableHeadItem: React.SFC<TableHeadItemProps> = (props) => {
  const [sortOrder, setSortOrder] = useState<SortOrder>('ASC');

  const toggleSortOrder = () => {
    const newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortOrder(newSortOrder);
    if (props.onClick) props.onClick(newSortOrder);
  };

  if (props.children && !props.hideSort) {
    return (
      <div
        role="presentation"
        className="table-thead__item grid"
        onClick={toggleSortOrder}
        data-head={props.dataHead}
      >
        {sortOrder === 'ASC' ? (
          <i className="icon-arrow-top" />
        ) : (
          <i className="icon-arrow-down" />
        )}
        {props.children}
      </div>
    );
  }
  if (props.children && props.hideSort) {
    return (
      <div
        role="presentation"
        className="table-thead__item grid"
        data-head={props.dataHead}
      >
        {props.children}
      </div>
    );
  }
  return (
    <>
      {!props.hideAction && (
        <div
          className="table-thead__item grid table-thead__item--actions"
          data-head={props.dataHead}
        />
      )}
    </>
  );
};

export const TableBody: React.SFC = (props) => {
  return <div className="table-tbody">{props.children}</div>;
};

export const TableBodyRow: React.SFC = (props) => {
  return <div className="table-tbody__row">{props.children}</div>;
};

interface TableBodyItemProps {
  children: any;
  dataTitle?: string;
  className?: string;
}

export const TableBodyItem: React.SFC<TableBodyItemProps> = (props) => {
  if (props.dataTitle) {
    return (
      <div
        className={`table-tbody__item ${props.className || ''}`}
        data-title={props.dataTitle}
      >
        {props.children}
      </div>
    );
  }

  return (
    <div className="table-tbody__item table-tbody__item--actions">
      {props.children}
    </div>
  );
};

export interface DataTableAction {
  label: string;
  onClick: (record: any) => void;
}

export interface DataTableProps {
  records: any[];
  children:
    | React.ReactElement<FieldProps<any>>
    | Array<React.ReactElement<FieldProps<any>>>;
  actions?: DataTableAction[];
  showActionsIf?: (record: any) => boolean;
}

export const DataTable: React.SFC<DataTableProps> = (props: DataTableProps) => {
  const renderHeadItem = (field: any) => {
    return (
      <TableHeadItem
        key={field.props.source}
        hideSort
        dataHead={field.props.label}
      >
        {field.props.label}
      </TableHeadItem>
    );
  };

  const renderActions = (record: any) => {
    if (!props.actions || props.actions.length === 0) {
      return <></>;
    }
    return (
      <TableBodyItem>
        {(!props.showActionsIf || props.showActionsIf(record)) &&
          props.actions.map((action) => (
            <Button
              onClick={() => action.onClick(record)}
              type="button"
              size="xs"
              light
            >
              {action.label}
            </Button>
          ))}
      </TableBodyItem>
    );
  };

  const renderListRow = (record: any) => {
    const fields = sanitizeChildren(props.children);
    return (
      <TableBodyRow key={record._id}>
        {fields.map((field, index) => {
          const value = findNestedObjectValue(
            record,
            field.props.source.split('.')
          );
          return (
            <TableBodyItem
              dataTitle={field.props.label}
              key={`${record._id}-${field.props.label}`}
              className={field.props.className}
            >
              <field.props.component
                {...{
                  ...field.props,
                  value,
                  record,
                  itemPosition: index,
                }}
              />
            </TableBodyItem>
          );
        })}
        {renderActions(record)}
      </TableBodyRow>
    );
  };
  const renderEmptyRow = () => {
    return (
      <TableBodyRow key={'empty_row'}>
        <div className="m-auto p-2 text-black-50">
          <TextView value={'No Data Found'}></TextView>
        </div>
      </TableBodyRow>
    );
  };

  return (
    <Table>
      <TableHead>
        {React.Children.map(props.children, renderHeadItem)}
        {props.actions && props.actions.length > 0 && <TableHeadItem />}
      </TableHead>
      <TableBody>
        {props.records.length >= 0 && props.records.map(renderListRow)}
        {props.records.length === 0 && renderEmptyRow()}
      </TableBody>
    </Table>
  );
};

export default {
  Table,
  TableHead,
  TableHeadItem,
  TableBody,
  TableBodyRow,
  TableBodyItem,
  DataTable,
};
